import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Popover,
  Button,
  Switch,
  Stack,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import GridViewIcon from '@mui/icons-material/GridView';

const Header = ({
  Icon,
  title,
  showAddButton,
  addButtonText,
  onAddClick,
  showActions,
  actionOptions,
  selectedAction,
  onActionChange,
  onDeleteClick,
  onRefresh,
  openFilter,
  handleFilterClick,
  handleFilterClose,
  anchorElFilter,
  handleSortClick,
  handleSort,
  openSort,
  anchorElSort,
  handleSortClose,
  handleOptionsClick,
  columns,
  openOptions,
  anchorElOptions,
  handleOptionsClose,
  handleAttributesClick,
  handleSettingsClick,
  openAttributes,
  anchorElAttributes,
  handleAttributesClose,
  visibilityStatus,
  handleToggle,
  selectedRows,
  onModifyClick,
  object,
  layoutView,
  onLayoutChange,
  openLayout,
  anchorElLayout,
  handleLayoutClick,
  handleLayoutClose,
}) => {
  const theme = useTheme();

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: "#1890ff",
          ...theme.applyStyles("dark", {
            backgroundColor: "#177ddc",
          }),
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], {
        duration: 200,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: "rgba(0,0,0,.25)",
      boxSizing: "border-box",
      ...theme.applyStyles("dark", {
        backgroundColor: "rgba(255,255,255,.35)",
      }),
    },
  }));

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
        {Icon && (
          <Icon
            style={{
              height: "32px",
              width: "32px",
              color: theme?.palette?.text?.primary,
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 400,
            color: theme.palette.text.primary
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {showAddButton && (
          <Button
            sx={{
              fontSize: 14,
              color: "#fff",
              height: "36px",
              fontWeight: 400,
              padding: "0px 16px",
              whiteSpace: "nowrap",
              minWidth: "auto",
              textTransform: "none",
              backgroundColor:
                selectedRows?.length > 0
                  ? theme?.palette?.tertiary.main
                  : "#006BCE",
              "&:hover": { backgroundColor: "#0058A5" },
              "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
            }}
            disabled={selectedRows?.length > 0}
            startIcon={<AddIcon />}
            onClick={onAddClick}
          >
            {addButtonText}
          </Button>
        )}
        {showActions && (
          <FormControl sx={{ width: 150, margin: "0 10px" }}>
            <Select
              value=""
              // onChange={onActionChange}
              displayEmpty
              renderValue={() => (
                <span style={{ color: "#006BCE", fontSize: "14px" }}>
                  Actions
                </span>
              )}
              sx={{
                borderRadius: "8px",
                fontSize: "14px",
                color: "#006BCE",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#006BCE",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#006BCE",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#006BCE",
                },
                "& .MuiSvgIcon-root": { color: "#006BCE" },
                "& .MuiSelect-select": {
                    padding: "8px 12px",
                  },
              }}
            >
              {actionOptions?.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ fontSize: "14px" }}
                  onClick={() => {
                    if (option.label === "Delete") {
                      onDeleteClick();
                    }
                    if (option.label === "Modify") {
                      onModifyClick();
                    }
                  }}
                  disabled={
                    (option.label === "Modify" && (selectedRows?.length === 0 || selectedRows?.length > 1)) ||
                    (option.label === "Delete" && selectedRows?.length === 0)
                  }
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <IconButton onClick={onRefresh}>
          <RefreshIcon />
        </IconButton>
        <IconButton onClick={handleFilterClick}>
          <FilterListIcon />
        </IconButton>
        <IconButton onClick={handleOptionsClick}>
          <MoreHorizIcon />
        </IconButton>
      </Box>

      {/* filter popup */}
      <Popover
        open={openFilter}
        anchorEl={anchorElFilter}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            padding: "12px 14px",
            bgcolor: theme?.palette?.secondary.main,
            color: "#fff",
            width: "200px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: theme?.palette?.text.primary,
              }}
            >
              Filter
            </Typography>
            <CloseIcon
              sx={{
                color: theme.palette?.text.primary,
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={handleFilterClose}
            />
          </Box>
          <Divider
            sx={{
              backgroundColor: theme.palette.border.color,
              boxSizing: "border-box",
              padding: "0px 14px",
            }}
          />
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme.palette.primary.main },
            }}
          >
            Last Scanned
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme.palette.primary.main },
            }}
          >
            Status
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme.palette.primary.main },
            }}
          >
            Type
          </Button>
        </Box>
      </Popover>

      {/* sort popover */}
      <Popover
        open={openSort}
        anchorEl={anchorElSort}
        onClose={handleSortClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            padding: "12px 14px",
            bgcolor: theme?.palette?.secondary.main,
            color: "#fff",
            width: "200px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: theme?.palette?.text?.primary,
              }}
            >
              Sort
            </Typography>
            <CloseIcon
              sx={{
                color: theme?.palette?.text?.primary,
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={handleSortClose}
            />
          </Box>
          <Divider
            sx={{
              backgroundColor: theme?.palette?.border.color,
              boxSizing: "border-box",
              padding: "0px 14px",
            }}
          />
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme?.palette?.primary.main },
            }}
            onClick={() => handleSort("asc")}
          >
            By Ascending
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme?.palette?.primary.main },
            }}
            onClick={() => handleSort("dsc")}
          >
            By Descending
          </Button>
        </Box>
      </Popover>

      {/* more options popover */}
      <Popover
        open={openOptions}
        anchorEl={anchorElOptions}
        onClose={handleOptionsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "12px 14px",
            gap: 1,
            bgcolor: theme?.palette?.secondary.main,
            color: "#fff",
            width: "200px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: theme?.palette?.text?.primary,
              }}
            >
              Options
            </Typography>
            <CloseIcon
              sx={{
                color: theme?.palette?.text?.primary,
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={handleOptionsClose}
            />
          </Box>
          <Divider
            sx={{
              backgroundColor: theme?.palette?.border.color,
              boxSizing: "border-box",
              padding: "0px 14px",
            }}
          />
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme?.palette?.primary.main },
            }}
          >
            Export
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme?.palette?.primary.main },
            }}
            onClick={handleAttributesClick}
          >
            Attributes
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              "&:hover": { bgcolor: theme?.palette?.primary.main },
            }}
            onClick={handleSettingsClick}
          >
            Settings
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              textAlign: "left",
              padding: "6px 14px",
              width: "100%",
              dispaly: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              "&:hover": { bgcolor: theme?.palette?.primary.main },
            }}
              onClick={handleLayoutClick}
          >
            <span>Layout</span>
            <Box sx={{display:'flex'}}>
              <span sx={{ fontSize: "12px", color: "#fff", opacity: 0.7 }}>{layoutView} </span>
              <ChevronRightIcon sx={{ fontSize: "20px", color: "#fff" }} />
            </Box>
          </Button>
        </Box>
      </Popover>

      {/* layout popover */}
      <Popover
        open={openLayout}
        anchorEl={anchorElLayout}
        onClose={handleLayoutClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "12px 14px",
            gap: 1,
            paddingRight: "14px",
            bgcolor: theme?.palette?.background?.paper || "#fff",
            color: theme?.palette?.text?.primary,
            width: "200px",
            border: `1px solid ${theme?.palette?.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={handleLayoutClose}
            >
              <NavigateBeforeIcon
                sx={{
                  color: theme?.palette?.text?.primary,
                  fontSize: "20px",
                }}
              />
              <Typography
                sx={{
                  color: theme?.palette?.text?.primary,
                  fontWeight: 500,
                }}
              >
                Layout
              </Typography>
            </Box>
            <CloseIcon
              sx={{
                color: theme?.palette?.text?.primary,
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={handleLayoutClose}
            />
          </Box>
          <Divider />
          {/* Layout Options */}
          <Box sx={{ display: "flex", gap: 1, paddingY: 1 }}>
            <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              // padding: "6px 14px",
              width: "100%",
              // "&:hover": { bgcolor: theme?.palette?.action?.hover || "#f5f5f5" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: `1px solid ${theme?.palette?.divider}`,
              "&:hover": {
                borderColor: "blue",
                "& .MuiSvgIcon-root": {
                  color: "blue",
                },
                "& .MuiButton-label": {
                  color: "blue",
                },
              },
            }}
            onClick={() => {
              onLayoutChange("list")
              handleLayoutClose()
            }}
          >
            <MenuIcon sx={{ fontSize: "20px" }} />
            <span sx={{ fontSize: "6px" }}>List View</span>
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: theme?.palette?.text?.primary,
              justifyContent: "flex-start",
              // padding: "6px 14px",
              width: "100%",
              // "&:hover": { bgcolor: theme?.palette?.action?.hover || "#f5f5f5" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: `1px solid ${theme?.palette?.divider}`,
              "&:hover": {
                borderColor: "blue",
                "& .MuiSvgIcon-root": {
                  color: "blue",
                },
                "& .MuiButton-label": {
                  color: "blue",
                },
              },
            }}
            onClick={() => {
              onLayoutChange("grid")
              handleLayoutClose()
            }}
          >
            <GridViewIcon sx={{ fontSize: "20px" }} />
            <span sx={{ fontSize: "6px" }}>Grid View</span>
          </Button>
          </Box>
        </Box>
      </Popover>

      {/* attributes popover */}
      <Popover
        open={openAttributes}
        anchorEl={anchorElAttributes}
        onClose={handleAttributesClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "12px 14px",
            gap: 1,
            paddingRight: "14px",
            bgcolor: theme?.palette?.secondary.main,
            color: "#fff",
            width: "200px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={handleAttributesClose}
            >
              <NavigateBeforeIcon
                sx={{
                  color: theme?.palette?.text?.primary,
                  fontSize: "20px",
                }}
              />
              <Typography
                sx={{
                  color: theme?.palette?.text?.primary,
                }}
              >
                Attributes
              </Typography>
            </Box>
            <CloseIcon
              sx={{
                color: theme?.palette?.text?.primary,
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={handleOptionsClose}
            />
          </Box>
          <Divider
            sx={{
              backgroundColor: theme?.palette?.border.color,
              boxSizing: "border-box",
              padding: "0px 14px",
            }}
          />
          <Box>
            {columns?.map((column) => (
              <div key={column.id}>
                {column.display && (
                  <Box
                    key={column.id}
                    sx={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      sx={{
                        textTransform: "none",
                        color: theme?.palette?.text?.primary,
                        justifyContent: "flex-start",
                        textAlign: "left",
                        padding: "8px 0px",
                        width: "100%",
                      }}
                    >
                      {column.label}
                    </Button>
                    <Stack direction="row" sx={{ alignItems: "center" }}>
                      <AntSwitch
                        checked={visibilityStatus[column.id]}
                        onChange={handleToggle(column.id)}
                        inputProps={{ "aria-label": `Toggle ${column.label}` }}
                      />
                    </Stack>
                  </Box>
                )}
              </div>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default Header;
